import theme from "@shared/utils/themeColors";
import { useEffect, useRef, useState } from "react";
import { formatText } from "../../../shared/utils/textFormatting";
import {
  TestLine,
  TestPassage as TestPassageType,
} from "../types/test-detail.types";
import TextRenderer from "./TextRenderer";

interface TestPassageProps {
  passage: TestPassageType;
  className?: string;
  highlightedLines?: { start?: number; end?: number };
  highlightedQuestionNumber?: number;
  renderText?: boolean;
}

export default function TestPassage({
  passage,
  highlightedLines,
  highlightedQuestionNumber,
  renderText = false,
  className,
}: TestPassageProps) {
  const lineRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const [highlightedNumberElements, setHighlightedNumberElements] = useState<
    HTMLElement[]
  >([]);

  // Find and scroll to lines with highlighted question number
  useEffect(() => {
    if (highlightedQuestionNumber) {
      // Find all lines that contain the pattern [(N)]
      const pattern = `\\[\\(${highlightedQuestionNumber}\\)\\]`;
      const regex = new RegExp(pattern);

      // Find all matching lines
      const matchingLines = passage.lines
        .filter((line) => regex.test(line.text))
        .map((line) => line.number);

      // If we found matching lines, scroll to the first one
      if (matchingLines.length > 0) {
        const lineElement = lineRefs.current[matchingLines[0]];
        if (lineElement) {
          lineElement.scrollIntoView({ behavior: "smooth", block: "center" });

          // Find all number elements to highlight
          const numberElements = lineElement.querySelectorAll(
            `.question-number-${highlightedQuestionNumber}`
          );
          setHighlightedNumberElements(
            Array.from(numberElements) as HTMLElement[]
          );

          // Apply animation class
          numberElements.forEach((el) => {
            el.classList.add("highlight-pulse");
            // Remove animation class after animation completes
            setTimeout(() => {
              el.classList.remove("highlight-pulse");
            }, 2000); // 2 seconds for animation
          });
        }
      }
    } else {
      // Clear highlighted elements when question number is removed
      setHighlightedNumberElements([]);
    }
  }, [highlightedQuestionNumber, passage.lines]);

  // Scroll to highlighted lines
  useEffect(() => {
    if (highlightedLines?.start) {
      const lineElement = lineRefs.current[highlightedLines.start];
      if (lineElement) {
        lineElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [highlightedLines]);

  const getLineClassName = (lineNumber: number) => {
    const start = highlightedLines?.start || 0;
    const end =
      highlightedLines?.end !== undefined
        ? highlightedLines.end < start
          ? start
          : highlightedLines.end
        : start;

    const isHighlighted =
      highlightedLines && lineNumber >= start && lineNumber <= end;

    return `flex ${isHighlighted ? "bg-yellow-100 dark:bg-yellow-900/30" : ""}`;
  };

  const setLineRef = (lineNumber: number) => (el: HTMLDivElement | null) => {
    lineRefs.current[lineNumber] = el;
  };

  const renderLineNumber = (line: TestLine) => {
    const baseClass =
      "inline-block w-5 text-right text-xs text-gray-400 mr-3 select-none";

    if (line.number === 1) {
      return <span className={baseClass}>Line</span>;
    }

    if (line.number % 5 === 0) {
      return <span className={baseClass}>{line.number}</span>;
    }

    return <span className={baseClass}></span>;
  };

  // Custom formatText function that adds specific classes for question numbers
  const formatTextWithHighlight = (text: string) => {
    if (!text) return null;

    return formatText(text, highlightedQuestionNumber);
  };

  return (
    <>
      {passage?.lines
        ?.map((line) => line?.text?.trim() !== "")
        ?.some((line) => line) || (passage?.images?.length ?? 0) > 0 ? (
        <div
          className={`${theme.bg.primary} rounded-lg shadow p-6 mb-4 ${className}`}
        >
          {passage?.title && (
            <h3 className="text-lg font-semibold mb-4">{passage.title}</h3>
          )}
          <div className="columns-1 gap-8 text-sm leading-relaxed break-inside-avoid-column">
            {renderText ? (
              <TextRenderer
                text={
                  passage?.lines?.map((line) => line?.text)?.join("\n") || ""
                }
                allowedAttr={["class"]}
              />
            ) : (
              passage?.lines?.map(
                (line) =>
                  line?.text?.trim() !== "" && (
                    <div
                      key={line.number}
                      className={getLineClassName(line.number)}
                      ref={setLineRef(line.number)}
                    >
                      {renderLineNumber(line)}
                      <span className="flex-1">
                        {formatTextWithHighlight(line.text)}
                      </span>
                    </div>
                  )
              )
            )}
          </div>

          {/* Add CSS for the animation */}
          <style
            dangerouslySetInnerHTML={{
              __html: `
            @-webkit-keyframes jello-horizontal {
              0% {
                -webkit-transform: scale3d(1, 1, 1);
                        transform: scale3d(1, 1, 1);
              }
              30% {
                -webkit-transform: scale3d(1.25, 0.75, 1);
                        transform: scale3d(1.25, 0.75, 1);
              }
              40% {
                -webkit-transform: scale3d(0.75, 1.25, 1);
                        transform: scale3d(0.75, 1.25, 1);
              }
              50% {
                -webkit-transform: scale3d(1.15, 0.85, 1);
                        transform: scale3d(1.15, 0.85, 1);
              }
              65% {
                -webkit-transform: scale3d(0.95, 1.05, 1);
                        transform: scale3d(0.95, 1.05, 1);
              }
              75% {
                -webkit-transform: scale3d(1.05, 0.95, 1);
                        transform: scale3d(1.05, 0.95, 1);
              }
              100% {
                -webkit-transform: scale3d(1, 1, 1);
                        transform: scale3d(1, 1, 1);
              }
            }
            @keyframes jello-horizontal {
              0% {
                -webkit-transform: scale3d(1, 1, 1);
                        transform: scale3d(1, 1, 1);
              }
              30% {
                -webkit-transform: scale3d(1.25, 0.75, 1);
                        transform: scale3d(1.25, 0.75, 1);
              }
              40% {
                -webkit-transform: scale3d(0.75, 1.25, 1);
                        transform: scale3d(0.75, 1.25, 1);
              }
              50% {
                -webkit-transform: scale3d(1.15, 0.85, 1);
                        transform: scale3d(1.15, 0.85, 1);
              }
              65% {
                -webkit-transform: scale3d(0.95, 1.05, 1);
                        transform: scale3d(0.95, 1.05, 1);
              }
              75% {
                -webkit-transform: scale3d(1.05, 0.95, 1);
                        transform: scale3d(1.05, 0.95, 1);
              }
              100% {
                -webkit-transform: scale3d(1, 1, 1);
                        transform: scale3d(1, 1, 1);
              }
            }


            .highlight-pulse {
              animation: jello-horizontal 1s ease-in-out;
              animation-iteration-count: 1;
            }
          `,
            }}
          />
          {/* Images */}
          {passage.images && passage.images.length > 0 && (
            <div className="mt-6 space-y-6">
              {passage.images.map((image, index) => (
                <div
                  key={`image-${index}-${image.storageUrl}`}
                  className="flex flex-col items-center"
                >
                  {image.title && (
                    <h4 className="text-base font-medium mb-2">
                      {image.title}
                    </h4>
                  )}
                  <div className="relative w-full max-w-2xl">
                    <img
                      src={image.storageUrl || image.url}
                      alt={image.title || image.caption || "Passage image"}
                      className="m-auto max-w-full object-contain rounded-md shadow-md"
                    />
                  </div>
                  {image.caption && (
                    <p className={`text-xs ${theme.text.muted}`}>
                      {image.caption}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </>
  );
}
